import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Select, Button } from "@/components/kit";
import { useQuickAdd } from "@/lib/quickAdd";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import ImageZoom from "@/components/ImageZoom";
import { Txt } from "@/components/Txt";

const box = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]";
const STATUS_BADGE = { yes: "bg-olive/25 text-dark-olive", no: "bg-maroon/15 text-maroon", maybe: "bg-gold/25 text-ink", awaiting: "bg-ink/10 text-ink/60" };
const statusLabel = (s) => (s === "awaiting" ? "awaiting" : s);
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const CONNECT = "connect@rosemoorchicago.org";

function ThreadRow({ mid, r, noReply, hasQuick, reloadParent }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(r.thread || []);
  useEffect(() => { setThread(r.thread || []); }, [r.thread]);
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/messages/${mid}/recipients/${r.user_id}/reply`, { text });
      setThread([...thread, { from: "sender", by_name: "You", text, at: new Date().toISOString() }]);
      setText(""); toast.success("Reply sent");
      if (reloadParent) reloadParent();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reply"); }
  };
  return (
    <div data-testid={`msg-recipient-${r.user_id}`} className="bg-cream border border-ink/10 rounded-lg px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold truncate">{r.name}</span>
        {!noReply && hasQuick && <span className={`text-[12px] px-2 py-0.5 rounded-full font-semibold capitalize shrink-0 ${STATUS_BADGE[r.status] || ""}`}>{statusLabel(r.status)}</span>}
      </div>
      {thread.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid={`thread-${r.user_id}`}>
          {thread.map((t, i) => (
            <div key={i} className={`text-[13px] rounded-lg px-2.5 py-1.5 ${t.from === "sender" ? "bg-maroon/8 border-l-2 border-maroon ml-6" : "bg-warm-cream border-l-2 border-dark-olive mr-6"}`}>
              <div className="text-[11px] font-semibold text-ink/50">{t.from === "sender" ? (t.by_name || "You") : r.name}</div>
              <div className="whitespace-pre-wrap">{t.text}</div>
            </div>
          ))}
        </div>
      )}
      {r.can_reply !== false && (
        open ? (
          <div className="mt-2 flex gap-2">
            <input data-testid={`sender-reply-text-${r.user_id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]" onKeyDown={(e) => e.key === "Enter" && send()} />
            <Button data-testid={`sender-reply-send-${r.user_id}`} onClick={send} className="px-3 min-h-[38px] text-[13px]">Send</Button>
          </div>
        ) : (
          <button data-testid={`sender-reply-open-${r.user_id}`} onClick={() => setOpen(true)} className="mt-2 text-[13px] text-maroon font-semibold flex items-center gap-1"><Icon name="MessageSquare" className="w-3.5 h-3.5" /> Reply in thread</button>
        )
      )}
    </div>
  );
}

function Detail({ mid, onClose }) {
  const [d, setD] = useState(null);
  const reload = useCallback(() => api.get(`/messages/${mid}`).then((r) => setD(r.data)).catch(() => {}), [mid]);
  useEffect(() => { reload(); }, [reload]);
  if (!d) return null;
  const methods = d.response_methods || [];
  const responseText = d.no_reply_needed ? "No reply needed" : (methods.length ? methods.map((m) => (m === "reply" ? "Written reply" : m)).join(", ") : "No response requested");
  const routeText = d.reply_enabled ? (d.reply_route === "crm" ? "kept in the CRM" : `emailed to ${d.reply_to}`) : "";
  const hasQuick = (d.quick_methods || []).length > 0;
  return (
    <div data-testid="message-detail">
      <button onClick={onClose} data-testid="message-detail-close" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-ink/70 hover:text-ink mb-3"><Icon name="ChevronLeft" className="w-4 h-4" /> Back to Messages</button>
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold leading-tight mb-4 max-w-2xl">{d.subject}</h1>
      <div className="space-y-3 max-w-2xl">
          <div className="text-[14px] text-ink/70 whitespace-pre-wrap">{d.note}</div>
          {d.image_url && <ImageZoom src={d.image_url} testid="detail-message-image" className="max-w-full max-h-96 rounded-lg border border-ink/15" />}
          <div className="text-[12px] text-ink/50">Attributed to {d.attribution === "association" ? "Rosemoor Community Association" : d.sender_name} · {fmt(d.created_at)}</div>
          <div className="text-[12px] text-ink/50" data-testid="detail-response-mode">Responses: {responseText}{routeText ? ` · replies ${routeText}` : ""}</div>
          <div className="text-[13px] font-semibold text-ink/60 pt-2">Recipients ({d.recipients.length})</div>
          <div className="space-y-1.5" data-testid="message-recipients">
            {d.recipients.map((r, i) => <ThreadRow key={r.user_id || i} mid={mid} r={r} noReply={d.no_reply_needed} hasQuick={hasQuick} reloadParent={reload} />)}
          </div>
      </div>
    </div>
  );
}

export default function TargetedMessages({ me }) {
  const [mode, setMode] = useState("people");
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [groupType, setGroupType] = useState("everyone");
  const [groupTag, setGroupTag] = useState("");
  const [tags, setTags] = useState([]);
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [noReply, setNoReply] = useState(false);
  const [methods, setMethods] = useState({ yes: false, no: false, maybe: false, reply: false });
  const [replyToMode, setReplyToMode] = useState("connect");
  const [replyToCustom, setReplyToCustom] = useState("");
  const [attribution, setAttribution] = useState("sender");
  const [imageUrl, setImageUrl] = useState("");
  const [imgBusy, setImgBusy] = useState(false);
  const uploadImage = async (file) => {
    if (!file) return; setImgBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/messages/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImageUrl(r.data.image_url); toast.success("Photo added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not upload photo"); }
    setImgBusy(false);
  };
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  useQuickAdd("targeted_messages", () => {
    document.querySelector('[data-testid="compose"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.querySelector('[data-testid="msg-subject"]')?.focus(), 300);
  });

  const load = useCallback(async () => {
    const [m, g] = await Promise.all([api.get("/messages"), api.get("/messages/groups")]);
    setItems(m.data.items); setTags(g.data.tags || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const raw = sessionStorage.getItem("rca_compose_to");
    if (raw) {
      try { const p = JSON.parse(raw); setMode("people"); setPicked([p]); } catch (e) {}
      sessionStorage.removeItem("rca_compose_to");
    }
    const many = sessionStorage.getItem("rca_compose_people");
    if (many) {
      try { const arr = JSON.parse(many); if (Array.isArray(arr) && arr.length) { setMode("people"); setPicked(arr); } } catch (e) {}
      sessionStorage.removeItem("rca_compose_people");
    }
  }, []);
  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(() => api.get("/messages/recipients/search", { params: { q } }).then((r) => setResults(r.data.people)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  const add = (p) => { if (!picked.find((x) => x.user_id === p.user_id)) setPicked([...picked, p]); setQ(""); setResults([]); };
  const toggleMethod = (k) => setMethods((mm) => ({ ...mm, [k]: !mm[k] }));

  const send = async () => {
    if (!subject.trim() || !note.trim()) return toast.error("Subject and message are required.");
    if (mode === "people" && picked.length === 0) return toast.error("Pick at least one person.");
    if (mode === "group" && groupType === "tag" && !groupTag) return toast.error("Choose a tag.");
    if (!noReply && methods.reply && replyToMode === "custom" && !replyToCustom.includes("@")) return toast.error("Enter a valid reply-to email.");
    const response_methods = noReply ? [] : Object.keys(methods).filter((k) => methods[k]);
    setSending(true);
    try {
      await api.post("/messages", {
        recipient_mode: mode, user_ids: picked.map((p) => p.user_id),
        group_type: mode === "group" ? groupType : null, group_tag: groupType === "tag" ? groupTag : null,
        subject, note,
        no_reply_needed: noReply,
        response_methods,
        reply_to_mode: !noReply && methods.reply ? replyToMode : null,
        reply_to_custom: !noReply && methods.reply && replyToMode === "custom" ? replyToCustom : null,
        attribution,
        image_url: imageUrl || null,
      });
      toast.success("Message sent");
      setSubject(""); setNote(""); setPicked([]); setNoReply(false); setImageUrl("");
      setMethods({ yes: false, no: false, maybe: false, reply: false }); setReplyToCustom("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setSending(false);
  };

  const Check = ({ k, label, tid }) => (
    <label className={`flex items-center gap-2 text-[14px] px-3 py-2 rounded-lg border cursor-pointer ${methods[k] ? "bg-ink/8 border-ink/30" : "border-ink/15"} ${noReply ? "opacity-40 pointer-events-none" : ""}`}>
      <input type="checkbox" data-testid={tid} checked={methods[k]} onChange={() => toggleMethod(k)} className="w-4 h-4 accent-maroon" />
      {label}
    </label>
  );

  useEffect(() => {
    const mid = sessionStorage.getItem("rca_open_message");
    if (mid) { sessionStorage.removeItem("rca_open_message"); setOpenId(mid); }
  }, []);

  if (openId) return <Detail mid={openId} onClose={() => { setOpenId(null); load(); }} />;

  return (
    <div data-testid="targeted-messages-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="msg.title" screen="Messages">Targeted Messages</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="msg.subtitle" screen="Messages">Post a note to a person or a group. It appears on their dashboard — choose how (or if) they can respond.</Txt></p>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 max-w-2xl mb-8" data-testid="compose">
        <div className="flex gap-2 mb-3">
          <button data-testid="mode-people" onClick={() => setMode("people")} className={`rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold border ${mode === "people" ? "bg-ink text-cream border-ink" : "border-ink/20"}`}>Specific people</button>
          <button data-testid="mode-group" onClick={() => setMode("group")} className={`rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold border ${mode === "group" ? "bg-ink text-cream border-ink" : "border-ink/20"}`}>A group</button>
        </div>

        {mode === "people" ? (
          <div className="mb-3">
            <input data-testid="recipient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members with an account…" className={box} />
            {results.length > 0 && <div className="border border-ink/15 rounded-lg mt-1 max-h-40 overflow-y-auto bg-white">{results.map((p) => <button key={p.user_id} data-testid={`recipient-opt-${p.user_id}`} onClick={() => add(p)} className="w-full text-left px-3 py-2 text-[14px] hover:bg-warm-cream">{p.name} <span className="text-ink/45 text-[12px]">{p.member_number}</span></button>)}</div>}
            {picked.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-testid="picked-list">{picked.map((p) => <span key={p.user_id} className="inline-flex items-center gap-1 bg-ink/8 rounded-full px-2.5 py-1 text-[13px]">{p.name}<button onClick={() => setPicked(picked.filter((x) => x.user_id !== p.user_id))}><Icon name="X" className="w-3 h-3" /></button></span>)}</div>}
          </div>
        ) : (
          <div className="mb-3 flex gap-2 flex-wrap">
            <Select data-testid="group-type" value={groupType} onChange={(e) => setGroupType(e.target.value)} className={box + " max-w-[240px]"}>
              <option value="everyone">Everyone with an account</option>
              <option value="tag">A tag</option>
              <option value="board">Board members</option>
            </Select>
            {groupType === "tag" && <Select data-testid="group-tag" value={groupTag} onChange={(e) => setGroupTag(e.target.value)} className={box + " max-w-[220px]"}><option value="">Choose a tag…</option>{tags.map((t) => <option key={t} value={t}>{t}</option>)}</Select>}
            {groupType === "everyone" && <span className="text-[12px] text-ink/50 self-center">Every active account — all member types, board, and admins.</span>}
          </div>
        )}

        <input data-testid="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={box + " mb-2"} />
        <textarea data-testid="msg-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Your message…" className={box + " mb-3"} />

        <div className="mb-3" data-testid="msg-image-area">
          {imageUrl && <img src={imageUrl} alt="" data-testid="msg-image-preview" className="max-h-40 rounded-lg border border-ink/15 mb-2" />}
          <div className="flex items-center gap-2">
            <label data-testid="msg-image-upload" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-dark-olive cursor-pointer hover:text-ink">
              <Icon name="Image" className="w-4 h-4" /> {imgBusy ? "Uploading…" : imageUrl ? "Replace photo" : "Add a photo"}
              <input type="file" accept="image/*" className="hidden" disabled={imgBusy} onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
            </label>
            {imageUrl && <button data-testid="msg-image-remove" onClick={() => setImageUrl("")} className="text-[13px] text-ink/50 hover:text-maroon">Remove</button>}
          </div>
        </div>

        {/* Response methods */}
        <div className="mb-3 border-t border-ink/10 pt-3">
          <div className="text-[13px] font-semibold text-ink/60 mb-2">How can people respond?</div>
          <label className="flex items-center gap-2 text-[14px] mb-2 font-semibold">
            <input type="checkbox" data-testid="no-reply-needed" checked={noReply} onChange={(e) => setNoReply(e.target.checked)} className="w-4 h-4 accent-maroon" />
            No reply needed (send with no response options)
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Check k="yes" label="Yes" tid="method-yes" />
            <Check k="no" label="No" tid="method-no" />
            <Check k="maybe" label="Maybe" tid="method-maybe" />
            <Check k="reply" label="Written reply" tid="method-reply" />
          </div>
          {!noReply && methods.reply && (
            <div className="mt-3 bg-warm-cream border border-ink/12 rounded-lg p-3" data-testid="reply-routing">
              <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Where should replies go?</div>
              <label className="flex items-center gap-2 text-[14px] mb-1"><input type="radio" data-testid="reply-connect" name="reply" checked={replyToMode === "connect"} onChange={() => setReplyToMode("connect")} /> The front desk ({CONNECT})</label>
              <label className="flex items-center gap-2 text-[14px] mb-1"><input type="radio" data-testid="reply-custom" name="reply" checked={replyToMode === "custom"} onChange={() => setReplyToMode("custom")} /> A specific email:</label>
              {replyToMode === "custom" && <input data-testid="reply-custom-input" value={replyToCustom} onChange={(e) => setReplyToCustom(e.target.value)} placeholder="name@example.com" className={box + " mt-1.5 mb-1"} />}
              <label className="flex items-center gap-2 text-[14px]"><input type="radio" data-testid="reply-crm" name="reply" checked={replyToMode === "crm"} onChange={() => setReplyToMode("crm")} /> Receive in CRM (kept here, never emailed out)</label>
            </div>
          )}
        </div>

        {/* Attribution */}
        <div className="mb-4 border-t border-ink/10 pt-3">
          <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Show this message as from</div>
          <label className="flex items-center gap-2 text-[14px] mb-1"><input type="radio" data-testid="attrib-sender" name="attrib" checked={attribution === "sender"} onChange={() => setAttribution("sender")} /> Me — {me?.user?.name || "my name"}</label>
          <label className="flex items-center gap-2 text-[14px]"><input type="radio" data-testid="attrib-association" name="attrib" checked={attribution === "association"} onChange={() => setAttribution("association")} /> Rosemoor Community Association (no individual name)</label>
        </div>

        <Button data-testid="msg-send" onClick={send} disabled={sending}>{sending ? "Sending…" : "Send message"}</Button>
      </div>

      <h2 className="font-zilla text-2xl font-semibold mb-3">Sent messages</h2>
      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="sent-list">
        {items.length === 0 && <div className="p-10 text-center text-ink/55">No messages sent yet.</div>}
        {items.map((m) => (
          <button key={m.message_id} data-testid={`sent-row-${m.message_id}`} onClick={() => setOpenId(m.message_id)} className="w-full text-left flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60">
            <div className="min-w-0"><div className="text-[16px] font-semibold truncate">{m.subject}</div><div className="text-[13px] text-ink/55">{m.recipient_count} recipient{m.recipient_count === 1 ? "" : "s"} · {fmt(m.created_at)}{m.attribution === "association" ? " · from RCA" : ""}</div></div>
            <div className="flex flex-wrap gap-1.5 shrink-0 text-[12px] font-semibold justify-end">
              {m.no_reply_needed ? (
                <span className="px-2 py-0.5 rounded-full bg-ink/10 text-ink/60">no reply needed</span>
              ) : (
                <>
                  {(m.quick_methods || []).includes("yes") && <span className="px-2 py-0.5 rounded-full bg-olive/25 text-dark-olive" data-testid={`count-yes-${m.message_id}`}>{m.counts.yes} yes</span>}
                  {(m.quick_methods || []).includes("no") && <span className="px-2 py-0.5 rounded-full bg-maroon/15 text-maroon">{m.counts.no} no</span>}
                  {(m.quick_methods || []).includes("maybe") && <span className="px-2 py-0.5 rounded-full bg-gold/25 text-ink">{m.counts.maybe} maybe</span>}
                  {m.reply_enabled && <span className="px-2 py-0.5 rounded-full bg-ink/10 text-ink/70">{m.counts.replied} replied</span>}
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
